// V14.13.10 — SIP PBX Extensions admin page (Phase 1).
//
// Lets hub_admin / super_admin manage external SIP/WebRTC registrar
// configs (3CX, Twilio SIP, Asterisk, etc.). Phase 1 is CRUD only —
// the SIP.js client that consumes /credentials lands in Phase 2.
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Phone, Plus, Edit2, Trash2, Server, KeyRound, Globe,
  CheckCircle2, XCircle, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import ConnectionsSubNav from '../../components/hub/ConnectionsSubNav';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PBX_PROVIDERS = [
  { value: '3cx', label: '3CX' },
  { value: 'twilio_sip', label: 'Twilio SIP' },
  { value: 'asterisk', label: 'Asterisk' },
  { value: 'freepbx', label: 'FreePBX' },
  { value: 'voipms', label: 'VoIP.ms' },
  { value: 'telnyx_sip', label: 'Telnyx SIP' },
  { value: 'custom', label: 'Custom / Other' },
];

const empty = () => ({
  name: '', pbx_provider: 'custom',
  registrar_uri: '', wss_url: '',
  sip_username: '', sip_password: '',
  display_name: '', realm: '', notes: '',
  ice_servers: [{ urls: 'stun:stun.l.google.com:19302', username: '', credential: '' }],
  assigned_user_ids: [], assigned_exercise_ids: [],
  enabled: true,
});

const fetchJson = async (url, opts = {}) => {
  const token = localStorage.getItem('ems_token');
  const r = await fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${r.status}`);
  }
  if (r.status === 204) return null;
  return r.json();
};

const SipExtensionsPage = () => {
  const [items, setItems] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // existing extension or null
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  const exerciseMap = React.useMemo(() => Object.fromEntries(exercises.map(e => [e.exercise_id, e.name])), [exercises]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, exs] = await Promise.all([
        fetchJson(`${API_URL}/hub/sip-extensions`),
        fetchJson(`${API_URL}/exercises`).catch(() => []),
      ]);
      setItems(data || []);
      setExercises(Array.isArray(exs) ? exs : (exs?.exercises || []));
    } catch (e) {
      toast.error(`Failed to load: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(empty());
    setDialogOpen(true);
  };

  const openEdit = (ext) => {
    setEditing(ext);
    setForm({
      ...empty(),
      ...ext,
      // Password stays blank in the form; leaving blank keeps the existing
      // encrypted password unchanged on save.
      sip_password: '',
      ice_servers: ext.ice_servers?.length > 0
        ? ext.ice_servers
        : [{ urls: 'stun:stun.l.google.com:19302' }],
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.registrar_uri.trim() || !form.wss_url.trim() || !form.sip_username.trim()) {
      toast.error('Name, Registrar URI, WSS URL and Username are required.');
      return;
    }
    if (!editing && !form.sip_password) {
      toast.error('SIP password is required for new extensions.');
      return;
    }
    setSaving(true);
    try {
      // Strip empty ice rows
      const ice_servers = (form.ice_servers || []).filter(s => s.urls?.trim());
      const payload = { ...form, ice_servers };
      if (editing) {
        await fetchJson(`${API_URL}/hub/sip-extensions/${editing.extension_id}`, {
          method: 'PUT', body: JSON.stringify(payload),
        });
        toast.success('SIP extension updated');
      } else {
        await fetchJson(`${API_URL}/hub/sip-extensions`, {
          method: 'POST', body: JSON.stringify(payload),
        });
        toast.success('SIP extension created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ext) => {
    if (!window.confirm(`Delete SIP extension "${ext.name}"?`)) return;
    try {
      await fetchJson(`${API_URL}/hub/sip-extensions/${ext.extension_id}`, { method: 'DELETE' });
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const setIce = (i, patch) => {
    const next = [...form.ice_servers];
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, ice_servers: next });
  };
  const addIce = () => setForm({ ...form, ice_servers: [...form.ice_servers, { urls: '' }] });
  const removeIce = (i) => setForm({ ...form, ice_servers: form.ice_servers.filter((_, j) => j !== i) });

  return (
    <div data-testid="sip-extensions-page">
      <ConnectionsSubNav />
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Server className="w-7 h-7" />
              SIP PBX Extensions
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              External SIP/WebRTC registrar configs (3CX, Twilio, Asterisk, etc.). Used by the Hub softphone for outbound/inbound calls.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="sip-reload-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={openCreate} data-testid="sip-create-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Extension
            </Button>
          </div>
        </div>

        {/* Empty / list */}
        {items.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              <Phone className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No SIP extensions yet.</p>
              <p className="text-xs mt-1">Add one to register the Hub softphone against an external PBX (3CX, Twilio, Asterisk, etc.).</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {items.map((ext) => (
              <Card key={ext.extension_id} data-testid={`sip-card-${ext.extension_id}`}>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold truncate">{ext.name}</h3>
                        <Badge variant="outline" className="text-[10px] uppercase">{ext.pbx_provider}</Badge>
                        {ext.enabled
                          ? <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 text-[10px]"><CheckCircle2 className="w-3 h-3 mr-1" />Enabled</Badge>
                          : <Badge variant="outline" className="text-muted-foreground text-[10px]"><XCircle className="w-3 h-3 mr-1" />Disabled</Badge>}
                      </div>
                      {ext.display_name && ext.display_name !== ext.name && (
                        <p className="text-xs text-muted-foreground mt-0.5">Display: {ext.display_name}</p>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(ext)} data-testid={`sip-edit-${ext.extension_id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => remove(ext)} data-testid={`sip-delete-${ext.extension_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <div className="flex items-center gap-1.5 truncate"><Globe className="w-3 h-3 shrink-0 text-muted-foreground" /><code className="truncate">{ext.registrar_uri}</code></div>
                    <div className="flex items-center gap-1.5 truncate"><Server className="w-3 h-3 shrink-0 text-muted-foreground" /><code className="truncate">{ext.wss_url}</code></div>
                    <div className="flex items-center gap-1.5"><KeyRound className="w-3 h-3 shrink-0 text-muted-foreground" /><span>user <code>{ext.sip_username}</code></span><span className={ext.has_password ? 'text-emerald-500' : 'text-red-500'}>{ext.has_password ? '· password set' : '· no password'}</span></div>
                    <div className="text-muted-foreground">{ext.ice_servers?.length || 0} ICE server(s)</div>
                  </div>
                  {/* V14.13.10 — show exercise scope so admins can tell at a glance which exercises this extension is wired to. */}
                  <div className="flex flex-wrap gap-1 pt-1">
                    {(!ext.assigned_exercise_ids || ext.assigned_exercise_ids.length === 0) ? (
                      <Badge variant="outline" className="text-[10px] bg-muted/40">Available in any exercise</Badge>
                    ) : (
                      ext.assigned_exercise_ids.map((ex) => (
                        <Badge key={ex} variant="outline" className="text-[10px] bg-blue-50 dark:bg-blue-950/30 border-blue-300 dark:border-blue-700">
                          {(exerciseMap[ex] || ex)}
                        </Badge>
                      ))
                    )}
                    {ext.assigned_user_ids?.length > 0 && (
                      <Badge variant="outline" className="text-[10px] bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700">
                        {ext.assigned_user_ids.length} user(s) only
                      </Badge>
                    )}
                  </div>
                  {ext.notes && <p className="text-xs text-muted-foreground italic line-clamp-2">{ext.notes}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create / Edit dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="sip-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit SIP extension' : 'New SIP extension'}</DialogTitle>
              <DialogDescription>
                {editing
                  ? 'Update connection details. Leave the password field blank to keep the existing one.'
                  : 'Connect the Hub softphone to an external PBX. The password is encrypted at rest and only decrypted server-side at registration time.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Name *</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ops Desk 101" data-testid="sip-form-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">PBX provider</Label>
                  <Select value={form.pbx_provider} onValueChange={(v) => setForm({ ...form, pbx_provider: v })}>
                    <SelectTrigger data-testid="sip-form-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PBX_PROVIDERS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Caller ID display name</Label>
                  <Input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} placeholder="Ops Desk" data-testid="sip-form-display" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Registrar URI *</Label>
                  <Input value={form.registrar_uri} onChange={e => setForm({ ...form, registrar_uri: e.target.value })} placeholder="sip:pbx.example.com:5060" data-testid="sip-form-reg" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">WSS URL *</Label>
                  <Input value={form.wss_url} onChange={e => setForm({ ...form, wss_url: e.target.value })} placeholder="wss://pbx.example.com:5061" data-testid="sip-form-wss" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">SIP username (extension) *</Label>
                  <Input value={form.sip_username} onChange={e => setForm({ ...form, sip_username: e.target.value })} placeholder="101" data-testid="sip-form-user" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">SIP password {editing ? '(blank = unchanged)' : '*'}</Label>
                  <Input type="password" value={form.sip_password} onChange={e => setForm({ ...form, sip_password: e.target.value })} placeholder={editing ? '••••••••' : 'Set a strong password'} data-testid="sip-form-pass" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">SIP realm (optional)</Label>
                  <Input value={form.realm || ''} onChange={e => setForm({ ...form, realm: e.target.value })} placeholder="pbx.example.com" data-testid="sip-form-realm" />
                </div>
              </div>

              {/* V14.13.10 — Exercise scoping. Empty = global; otherwise the
                  extension only works for users currently in one of these
                  exercises. Enforced server-side in /credentials. */}
              <div className="space-y-2 pt-2 border-t border-border">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Scope to exercises (empty = available everywhere)
                  </Label>
                  {form.assigned_exercise_ids?.length > 0 && (
                    <Button type="button" size="sm" variant="ghost"
                      onClick={() => setForm({ ...form, assigned_exercise_ids: [] })}
                      data-testid="sip-form-clear-exercises">
                      Clear
                    </Button>
                  )}
                </div>
                <div className="max-h-40 overflow-y-auto rounded-md border border-input bg-muted/30 p-2 space-y-1">
                  {exercises.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2 text-center">No exercises available.</p>
                  ) : (
                    exercises.map((ex) => {
                      const checked = form.assigned_exercise_ids.includes(ex.exercise_id);
                      return (
                        <label key={ex.exercise_id}
                          className="flex items-center gap-2 py-1 cursor-pointer text-sm"
                          data-testid={`sip-form-exercise-${ex.exercise_id}`}>
                          <input type="checkbox" checked={checked}
                            onChange={(e) => setForm({
                              ...form,
                              assigned_exercise_ids: e.target.checked
                                ? [...form.assigned_exercise_ids, ex.exercise_id]
                                : form.assigned_exercise_ids.filter(id => id !== ex.exercise_id),
                            })} />
                          <span className="truncate">{ex.name}</span>
                          <code className="ml-auto text-[10px] text-muted-foreground">{ex.exercise_id}</code>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              {/* ICE servers */}
              <div className="space-y-2 pt-2 border-t border-border">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">ICE Servers (STUN / TURN)</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addIce} data-testid="sip-form-addice">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                  </Button>
                </div>
                {form.ice_servers.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2" data-testid={`sip-form-ice-${i}`}>
                    <Input className="col-span-12 sm:col-span-6 h-8 text-xs" value={s.urls} onChange={(e) => setIce(i, { urls: e.target.value })} placeholder="stun:stun.l.google.com:19302  or  turn:turn.example.com:3478" />
                    <Input className="col-span-5 sm:col-span-3 h-8 text-xs" value={s.username || ''} onChange={(e) => setIce(i, { username: e.target.value })} placeholder="TURN user" />
                    <Input className="col-span-5 sm:col-span-2 h-8 text-xs" value={s.credential || ''} onChange={(e) => setIce(i, { credential: e.target.value })} placeholder="TURN pass" />
                    <Button type="button" variant="ghost" size="icon" className="col-span-2 sm:col-span-1 h-8 w-8" onClick={() => removeIce(i)}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Notes</Label>
                <Textarea rows={2} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional internal note" data-testid="sip-form-notes" />
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border">
                <Label htmlFor="sip-enabled" className="text-sm">Enabled</Label>
                <Switch id="sip-enabled" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="sip-form-enabled" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="sip-form-save">
                {saving ? 'Saving…' : (editing ? 'Save changes' : 'Create extension')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SipExtensionsPage;
